import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages pickup availability display and drawer.
 * Updates pickup information when product variants change.
 *
 * @extends {DialogComponent}
 */
class PickupAvailabilityComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    
    this.productUrl = this.getAttribute('data-product-url');
    this.sectionId = this.getAttribute('data-section-id');
    
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantChange);
  }

  /**
   * Shows the pickup availability drawer.
   */
  showDrawer() {
    this.showDialog();
  }

  /**
   * Closes the pickup availability drawer.
   */
  closeDrawer() {
    this.closeDialog();
  }

  /**
   * Handles variant update events and updates pickup availability.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event
   */
  #handleVariantChange = async (event) => {
    const variant = event.detail?.resource;
    const variantId = variant?.id;
    
    if (!variantId) {
      this.setAttribute('hidden', '');
      this.closeDrawer();
      return;
    }

    try {
      await this.#fetchPickupAvailability(variantId);
    } catch (error) {
      console.error('Error fetching pickup availability:', error);
    }
  };

  /**
   * Fetches pickup availability for a specific variant.
   * @param {number} variantId - The variant ID
   */
  async #fetchPickupAvailability(variantId) {
    if (!this.productUrl || !this.sectionId) return;

    const url = `${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`;
    
    try {
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const newPickupAvailability = doc.querySelector('pickup-availability-component');
      
      if (newPickupAvailability) {
        const isHidden = newPickupAvailability.hasAttribute('hidden');
        
        if (isHidden) {
          this.setAttribute('hidden', '');
        } else {
          this.removeAttribute('hidden');
          
          const newSummary = newPickupAvailability.querySelector('.pickup-availability__summary');
          const newDrawerContent = newPickupAvailability.querySelector('.pickup-availability__drawer-content');
          const newDrawerTitle = newPickupAvailability.querySelector('.pickup-availability__drawer-title');
          
          const currentSummary = this.querySelector('.pickup-availability__summary');
          const currentDrawerContent = this.querySelector('.pickup-availability__drawer-content');
          const currentDrawerTitle = this.querySelector('.pickup-availability__drawer-title');
          
          if (newSummary && currentSummary) {
            currentSummary.innerHTML = newSummary.innerHTML;
          }
          
          if (newDrawerContent && currentDrawerContent) {
            currentDrawerContent.innerHTML = newDrawerContent.innerHTML;
          }
          
          if (newDrawerTitle && currentDrawerTitle) {
            currentDrawerTitle.innerHTML = newDrawerTitle.innerHTML;
          }
          
          this.setAttribute('data-variant-id', variantId);
        }
      } else {
        this.setAttribute('hidden', '');
      }
    } catch (error) {
      console.error('Failed to fetch pickup availability:', error);
      this.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('pickup-availability-component')) {
  customElements.define('pickup-availability-component', PickupAvailabilityComponent);
}
